import { makeAutoObservable } from "mobx";
import { makePersistable } from "mobx-persist-store";
import localForage from "localforage";
import { TallyDraft } from "../Models/TallyDraft";
import { TallyRecord } from "../Models/TallyRecord";

export class TallyStore {
  private _drafts: TallyDraft[] = [];

  constructor() {
    makeAutoObservable(this);
    makePersistable(this, {
      name: "pda-picking-rego-tally",
      properties: ["_drafts"] as (keyof this)[],
      storage: localForage,
      stringify: false,
    });
  }

  public get drafts(): TallyDraft[] {
    return this._drafts;
  }

  public set drafts(value: TallyDraft[]) {
    this._drafts = value;
  }

  private isSameLine(draft: TallyDraft, serverUrl: string, dbKey: string, transRefCode: string, detailNo: number): boolean {
    return draft.serverUrl === serverUrl && draft.dbKey === dbKey && draft.transRefCode === transRefCode && draft.detailNo === detailNo;
  }

  public getDraft(serverUrl: string, dbKey: string, transRefCode: string, detailNo: number): TallyDraft | undefined {
    return this._drafts.find((d) => this.isSameLine(d, serverUrl, dbKey, transRefCode, detailNo));
  }

  public getDraftsForTransaction(serverUrl: string, dbKey: string, transRefCode: string): TallyDraft[] {
    return this._drafts.filter((d) => d.serverUrl === serverUrl && d.dbKey === dbKey && d.transRefCode === transRefCode);
  }

  public saveDraft(draft: TallyDraft): void {
    const others = this._drafts.filter((d) => !this.isSameLine(d, draft.serverUrl, draft.dbKey, draft.transRefCode, draft.detailNo));
    this._drafts = [...others, draft];
  }

  public addTally(
    serverUrl: string,
    dbKey: string,
    transRefCode: string,
    detailNo: number,
    tally: TallyRecord,
    stockCode?: string,
    stockNo?: string,
  ): TallyDraft {
    const existing = this.getDraft(serverUrl, dbKey, transRefCode, detailNo);
    const draft: TallyDraft = existing
      ? { ...existing, tallies: [...existing.tallies, tally], savedAt: new Date().toISOString() }
      : {
          serverUrl,
          dbKey,
          transRefCode,
          detailNo,
          stockCode,
          stockNo,
          tallies: [tally],
          posted: false,
          savedAt: new Date().toISOString(),
        };
    this.saveDraft(draft);
    return draft;
  }

  //remembers that the operator chose Ignore on the missing stock barcode prompt for this line.
  //Upserts, so it works whether or not the scan that raised the prompt has been tallied yet.
  public setIgnoreMissingStockBarcode(
    serverUrl: string,
    dbKey: string,
    transRefCode: string,
    detailNo: number,
    stockCode?: string,
    stockNo?: string,
  ): void {
    const existing = this.getDraft(serverUrl, dbKey, transRefCode, detailNo);
    const draft: TallyDraft = existing
      ? { ...existing, ignoreMissingStockBarcode: true }
      : {
          serverUrl,
          dbKey,
          transRefCode,
          detailNo,
          stockCode,
          stockNo,
          tallies: [],
          posted: false,
          ignoreMissingStockBarcode: true,
          savedAt: new Date().toISOString(),
        };
    this.saveDraft(draft);
  }

  public removeTally(serverUrl: string, dbKey: string, transRefCode: string, detailNo: number, tallyId: string): void {
    const existing = this.getDraft(serverUrl, dbKey, transRefCode, detailNo);
    if (!existing) {
      return;
    }
    //remove by id, not serial - the same label can be tallied more than once
    const tallies = existing.tallies.filter((t) => t.id !== tallyId);
    //an emptied draft goes, unless it is still holding the Ignore answer for this line
    if (tallies.length === 0 && !existing.ignoreMissingStockBarcode) {
      this.removeDraft(serverUrl, dbKey, transRefCode, detailNo);
      return;
    }
    this.saveDraft({ ...existing, tallies, savedAt: new Date().toISOString() });
  }

  public removeDraft(serverUrl: string, dbKey: string, transRefCode: string, detailNo: number): void {
    this._drafts = this._drafts.filter((d) => !this.isSameLine(d, serverUrl, dbKey, transRefCode, detailNo));
  }

  public removeDraftsForTransaction(serverUrl: string, dbKey: string, transRefCode: string): void {
    this._drafts = this._drafts.filter((d) => !(d.serverUrl === serverUrl && d.dbKey === dbKey && d.transRefCode === transRefCode));
  }

  public clear(): void {
    this._drafts = [];
  }
}

const tallyStore = new TallyStore();
export default tallyStore;
